import React, { useState, useEffect, useRef } from "react"
import "./GridView.css"
import { convertMinutesTo12HHMM } from "../../utils/generalUtils"
import { useSelector, useDispatch } from "react-redux"
import { usePermissionsList } from "../../hooks/queries/usePermissions"
import { startTimeAllCases } from "../../utils/realTimeUtils"
import EditPatient from "../NurseAssignments/EditPatient"
import {
	hasPermission,
	allowedToEdit,
	confirmedCancellationNoShow,
	currentlyRealTimeCompleted,
	currentlyRealTimeOngoing,
	currentlyRealTimeFuture
} from "../../utils/realTimeUtils"
import { getScheduleSelection, getScheduleForSelection } from "./CustomCalendar/scheduleSelection"
import { calculateNurseAcuity, calculateTotalAcuity } from "../../utils/realTimeNurseUtils"
import { getNursePatients } from "../../features/schedule/derivedAssignments"
import { FaCircleExclamation } from "react-icons/fa6"
import { Transition } from "@headlessui/react"
import { CiStickyNote } from "react-icons/ci"
import { IoClose } from "react-icons/io5"
import { ViewColumnsIcon } from "@heroicons/react/24/outline"
import { IoCheckmark } from "react-icons/io5"
import { IoPersonCircle } from "react-icons/io5"
import { FaExclamationCircle } from "react-icons/fa"
import { setNurseSchedule } from "../../features/schedule/scheduleSlice"
import { createSwapy } from "swapy"
import { RxDragHandleDots2 } from "react-icons/rx"
import PatientNurseStats from "./TopLevelStats"
import StatusFilter from "../NurseAssignments/StatusFilter"
import SearchBar from "../NurseAssignments/SearchBar"
import {
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	getDateFormatted,
	humanFormattedDate
} from "../../utils/generalUtils"
import { useAuth0 } from "@auth0/auth0-react"
import { FaPrint } from "react-icons/fa"
import { setColumnCount } from "../../features/users/userSlice"
import useLogging from "../../hooks/useLogging"
import useFetchSettings from "../../hooks/useFetchSettings"
import { throttle } from "lodash" // Import lodash for throttling

const getChairType = chair => {
	if (!chair) return "N/A"

	const chairType = chair.chairType
		? Object.keys(chair.chairType).find(type => chair.chairType[type]) || "Unknown Type"
		: "Unknown Type"
	return `${chairType}`
}

const getChairDetails = (chairSchedule, chairId) => {
	const chair = chairSchedule.find(chair => chair.chairId === chairId)
	if (!chair) return "N/A"
	return `${chair.chairName} (${getChairType(chair)})`
}

// Update this function to check if a patient is an add-on (booked today or tomorrow based on mode)
const isAddOn = (patient, centerOpen, test_environment, isTomorrowMode, centerClosedDates) => {
	if (!patient || !patient.bookedDate) return false

	const firstOpenDate = isTomorrowMode
		? getDateFormatted(
				getFirstOpenDateTomorrow(centerOpen, test_environment, centerClosedDates)
			)
		: getDateFormatted(getFirstOpenDateToday(centerOpen, test_environment, centerClosedDates))

	return firstOpenDate === patient.bookedDate
}

export default function GridView({
	patientSchedule,
	nurseSchedule,
	chairSchedule,
	chargeNurse,
	setNotification
}) {
	const { user, getAccessTokenSilently } = useAuth0()
	const dispatch = useDispatch()
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const { settingsData } = useFetchSettings()

	const { columnCount, colorScheme, viewMode, select } = useSelector(state => state.user)
	const [inputColumnValue, setInputColumnValue] = useState(columnCount || 4)
	const [isEditOpen, setIsEditOpen] = useState(false)
	const [taskToEdit, setTaskToEdit] = useState(null)
	const permissions = usePermissionsList()

	const scheduleSelection = getScheduleSelection(isTomorrowMode, sandbox)

	const currentSchedule = getScheduleForSelection(scheduleSelection)

	const logging = useLogging()

	const isEditingPatient = useSelector(state => state.schedule.isEditing)

	const [showNotification, setShowNotification] = useState(true)

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"

	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []

	const swapy = useRef(null)
	const container = useRef(null)

	const [gridViewNurseSchedule, setGridViewNurseSchedule] = useState(nurseSchedule)
	const [isDragging, setIsDragging] = useState(false)
	const [draggingItem, setDraggingItem] = useState(null)

	const [selectedStatuses, setSelectedStatuses] = useState([])

	useEffect(() => {
		// Check if nurse IDs have changed (additions or removals)
		const currentNurseIds = new Set(gridViewNurseSchedule.map(nurse => nurse.nurseId))
		const newNurseIds = new Set(nurseSchedule.map(nurse => nurse.nurseId))

		// If the sets are different sizes or contain different IDs, we need to rebuild the list
		const hasNurseChanges =
			currentNurseIds.size !== newNurseIds.size ||
			![...currentNurseIds].every(id => newNurseIds.has(id))

		if (hasNurseChanges) {
			// Keep only nurses that still exist in nurseSchedule while preserving order
			const existingNursesInOrder = gridViewNurseSchedule
				.filter(existingNurse => newNurseIds.has(existingNurse.nurseId))
				.map(existingNurse => {
					// Update with latest data
					const updatedNurse = nurseSchedule.find(
						nurse => nurse.nurseId === existingNurse.nurseId
					)
					return updatedNurse || existingNurse
				})

			// Add new nurses that don't exist in current gridViewNurseSchedule
			const newNurses = nurseSchedule.filter(nurse => !currentNurseIds.has(nurse.nurseId))

			setTimeout(() => {
				swapy.current?.update()
			}, 0)

			setGridViewNurseSchedule([...existingNursesInOrder, ...newNurses])
		} else {
			// Just update the existing nurses' data while maintaining order
			const updatedSchedule = gridViewNurseSchedule.map(existingNurse => {
				const updatedNurse = nurseSchedule.find(
					nurse => nurse.nurseId === existingNurse.nurseId
				)
				return updatedNurse || existingNurse
			})

			setGridViewNurseSchedule(updatedSchedule)
		}
		// `gridViewNurseSchedule` is intentionally omitted to avoid re-running this effect when we set it from inside.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nurseSchedule])

	useEffect(() => {
		if (container.current) {
			// If swapy instance already exists, destroy it first
			if (swapy.current) {
				swapy.current.destroy()
			}

			// Create new swapy instance with proper configuration
			try {
				swapy.current = createSwapy(container.current, {
					animation: "dynamic",
					swapMode: "hover",
					autoScrollOnDrag: false, // Disable built-in auto-scroll
					highlightClass: "bg-gray-100" // Add a highlight class
				})

				const handleMouseMove = throttle(event => {
					const scrollSpeed = 80 // Reduce this for smoother scrolling
					const threshold = 150 // Adjust this for the margin
					const mouseY = event.clientY
					const windowHeight = window.innerHeight

					if (mouseY < threshold) {
						window.scrollBy(0, -scrollSpeed)
					} else if (mouseY > windowHeight - threshold) {
						window.scrollBy(0, scrollSpeed)
					}
				}, 100) // Throttle the event to fire every 100ms

				const handleSwapStart = event => {
					if (
						event &&
						event.slotItemMap &&
						event.slotItemMap.asArray &&
						event.slotItemMap.asArray.length > 0
					) {
						setIsDragging(true)
						setDraggingItem(event.slotItemMap.asArray[0].item)
						window.addEventListener("mousemove", handleMouseMove)
					}
				}

				const handleSwapEnd = event => {
					if (event && event.slotItemMap && event.slotItemMap.asArray) {
						const arrayWithNurseIds = event.slotItemMap.asArray

						const reorderedNurseSchedule = arrayWithNurseIds.map(item => {
							return nurseSchedule.find(nurse => nurse.nurseId === item.item)
						})

						dispatch(setNurseSchedule(reorderedNurseSchedule))
					}

					setIsDragging(false)
					setDraggingItem(null)
					window.removeEventListener("mousemove", handleMouseMove)
				}

				swapy.current.onSwapStart(handleSwapStart)
				swapy.current.onSwapEnd(handleSwapEnd)
			} catch (error) {
				console.error("Error initializing swapy:", error)
			}
		}

		return () => {
			if (swapy.current) {
				try {
					window.removeEventListener(
						"mousemove",
						throttle(() => {}, 100)
					)
					swapy.current.destroy()
				} catch (error) {
					console.error("Error destroying swapy:", error)
				}
			}
		}
	}, [nurseSchedule, dispatch])

	useEffect(() => {
		// Use the columnCount from Redux store which is populated from Auth0 metadata
		if (columnCount) {
			setInputColumnValue(columnCount)
		}
		// Mount-only effect — intentionally runs once.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		// Load showNotification state from localStorage when the component mounts
		const savedShowNotification = localStorage.getItem("showNotification")
		if (savedShowNotification !== null) {
			setShowNotification(JSON.parse(savedShowNotification))
		}
	}, [])

	const handleInputChange = e => {
		const value = e.target.value
		setInputColumnValue(value)
	}

	const updateColumnCount = async () => {
		const newCount = parseInt(inputColumnValue)
		dispatch(setColumnCount(newCount))

		try {
			const token = await getAccessTokenSilently()

			// Make a POST request to the backend route to update the user's app_metadata
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
				// Replace with your actual endpoint
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}` // Include the user's auth token if needed
				},
				body: JSON.stringify({
					user_metadata: {
						colorScheme: colorScheme,
						viewMode: viewMode,
						select: select,
						columnCount: columnCount
					}
				})
			})

			if (!response.ok) {
				throw new Error("Failed to update user metadata")
			}
		} catch (error) {
			logging.error("Error updating user metadata:", {
				error: error.message,
				stack: error.stack
			})
		}
	}

	const closeNotification = () => {
		setShowNotification(false)
		localStorage.setItem("showNotification", false) // Save showNotification state to localStorage
	}

	if (!permissions) {
		return null
	}

	const openEditPopup = task => {
		if (isEditingPatient) {
		} else if (
			!sandbox &&
			(hasPermission(permissions, "write:schedules") ||
				hasPermission(permissions, "write:chairs") ||
				hasPermission(permissions, "read:schedule-personal") ||
				sandbox) &&
			!allowedToEdit(task, sandbox, isTomorrowMode)
		) {
			setNotification({
				visible: true,
				message: "You cannot edit this visit because it has ended.",
				success: false
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} else {
			logging.info("Opened Edit Patient Popup", {
				patient: task,
				nurse: nurseSchedule.find(nurse => nurse.nurseId === task.assignedNurse)
			})
			setTaskToEdit(task)
			setIsEditOpen(true)
		}
	}

	const closeEditPopup = () => {
		setIsEditOpen(false)
		setTaskToEdit(null)
	}

	const getChairZone = chairId => {
		const chair = settingsData.chairs.find(chair => chair.id === chairId)
		return chair?.zone || "N/A"
	}

	// Add this CSS class to your styles
	const animatedClass = "transition-height duration-300 ease-in-out"

	// Filter patients based on selected statuses
	const getFilteredPatients = (patients, statuses) => {
		if (statuses.length === 0) {
			return patients
		}

		return patients.filter(patient =>
			statuses.some(selected =>
				selected.value.some(val => patient.internalStatus?.includes(val))
			)
		)
	}

	// Update this function to handle search result clicks
	const handleSearchResultClick = result => {
		if (result.type === "nurse") {
			// Find the nurse element and scroll to it
			const nurseElement = document.querySelector(`[data-swapy-item="${result.id}"]`)
			if (nurseElement) {
				nurseElement.scrollIntoView({ behavior: "smooth", block: "center" })

				// Add a temporary highlight effect
				nurseElement.classList.add("ring-4", "ring-blue-500", "ring-opacity-75", "z-10")
				setTimeout(() => {
					nurseElement.classList.remove(
						"ring-4",
						"ring-blue-500",
						"ring-opacity-75",
						"z-10"
					)
				}, 2000)
			}
		} else if (result.type === "patient") {
			// Find the patient in the schedule
			const patient = result.originalItem

			// Find the patient row in the DOM
			const patientRows = document.querySelectorAll("tr")
			let patientRow = null

			// Loop through all rows to find the one with this patient
			patientRows.forEach(row => {
				if (row.textContent.includes(patient.patientName)) {
					patientRow = row
				}
			})

			if (patientRow) {
				// Scroll to the patient row
				patientRow.scrollIntoView({ behavior: "smooth", block: "center" })

				// Add a temporary highlight effect - using inward highlighting for patient rows
				patientRow.classList.add("bg-blue-100", "shadow-inner", "shadow-blue-500/50")

				// Also highlight all cells in the row
				const cells = patientRow.querySelectorAll("td")
				cells.forEach(cell => {
					cell.classList.add("bg-blue-100", "font-semibold")
				})

				setTimeout(() => {
					patientRow.classList.remove("bg-blue-100", "shadow-inner", "shadow-blue-500/50")

					cells.forEach(cell => {
						cell.classList.remove("bg-blue-100", "font-semibold")
					})
				}, 2000)

				// Also open the edit popup
				openEditPopup(patient)
			} else {
				// If we can't find the row, just open the edit popup
				openEditPopup(patient)
			}
		}
	}

	// Add this function to handle printing
	const handlePrint = () => {
		const printWindow = window.open("", "_blank")

		if (!printWindow) {
			setNotification({
				visible: true,
				message: "Please allow popups to print the schedule",
				success: false
			})
			return
		}

		// Fetch the role info data
		const fetchRoleInfo = async () => {
			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})

				// Determine the date based on mode
				const currentDate = new Date()
				let displayDate
				let date

				if (isTomorrowMode) {
					const tomorrow = getFirstOpenDateTomorrow(
						centerOpen,
						testEnvironment,
						centerClosedDates
					)
					displayDate = humanFormattedDate(tomorrow)
					date = getDateFormatted(tomorrow)
				} else {
					displayDate = humanFormattedDate(currentDate)
					date = getDateFormatted(
						getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)
					)
				}

				// Find the charge nurse name
				const chargeNurseName = chargeNurse ? chargeNurse.nurseName : "None assigned"

				// Fetch role info
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/api/get-role-info?date=${date}`,
					{ method: "GET", headers: { Authorization: `Bearer ${token}` } }
				)

				if (!response.ok) {
					throw new Error("Failed to load role info")
				}

				const data = await response.json()
				const roleInfo = data.extraNotes

				generatePrintContent(displayDate, chargeNurseName, roleInfo)
			} catch (error) {
				logging.error("Error fetching role info:", error)
				// If there's an error, still generate the print content without the role info
				const displayDate = isTomorrowMode
					? humanFormattedDate(new Date(new Date().setDate(new Date().getDate() + 1)))
					: humanFormattedDate(new Date())
				const chargeNurseName = chargeNurse ? chargeNurse.nurseName : "None assigned"
				generatePrintContent(displayDate, chargeNurseName, {})
			}
		}

		const generatePrintContent = (displayDate, chargeNurseName, roleInfo) => {
			// Format the role info into a 3-column layout
			const formatRoleInfoInColumns = roleInfo => {
				if (!roleInfo || Object.keys(roleInfo).length === 0) return ""

				// Create an array of role entries
				const roleEntries = []
				// Zone MAs grouped together
				if (roleInfo.zone1) roleEntries.push({ label: "Zone 1 MA", value: roleInfo.zone1 })
				if (roleInfo.zone2) roleEntries.push({ label: "Zone 2 MA", value: roleInfo.zone2 })
				if (roleInfo.zone3) roleEntries.push({ label: "Zone 3 MA", value: roleInfo.zone3 })
				if (roleInfo.zone4) roleEntries.push({ label: "Zone 4 MA", value: roleInfo.zone4 })
				// Triage and Resource MA grouped with other MAs
				if (roleInfo.triageRole)
					roleEntries.push({ label: "Triage MA", value: roleInfo.triageRole })
				if (roleInfo.resourceMA)
					roleEntries.push({ label: "Resource MA", value: roleInfo.resourceMA })
				// Nurse section
				if (roleInfo.nurseSchedulers)
					roleEntries.push({ label: "Nurse Schedulers", value: roleInfo.nurseSchedulers })
				if (roleInfo.resourceNurse)
					roleEntries.push({ label: "Resource Nurse", value: roleInfo.resourceNurse })
				// New On Call Supervisor field
				if (roleInfo.onCallSupervisor)
					roleEntries.push({
						label: "On Call Supervisor",
						value: roleInfo.onCallSupervisor
					})
				// Other roles
				if (roleInfo.ft) roleEntries.push({ label: "FT", value: roleInfo.ft })
				if (roleInfo.lab) roleEntries.push({ label: "Lab RN", value: roleInfo.lab })
				if (roleInfo.pas) roleEntries.push({ label: "PAs", value: roleInfo.pas })
				if (roleInfo.asc) roleEntries.push({ label: "ASC", value: roleInfo.asc })
				if (roleInfo.clinicLeaders)
					roleEntries.push({ label: "Clinic Leaders", value: roleInfo.clinicLeaders })
				if (roleInfo.closingMd)
					roleEntries.push({ label: "Closing MD", value: roleInfo.closingMd })
				if (roleInfo.deskRole)
					roleEntries.push({ label: "Desk Role", value: roleInfo.deskRole })
				if (roleInfo.pharmacy)
					roleEntries.push({ label: "Pharmacy", value: roleInfo.pharmacy })

				if (roleEntries.length === 0) return ""

				// Calculate how many items per column - more balanced distribution
				const itemsPerColumn = Math.ceil(roleEntries.length / 3)

				// Create the HTML for the columns
				let html = `<div class="role-info-columns">`

				// First column
				html += `<div class="role-info-column">`
				for (let i = 0; i < itemsPerColumn && i < roleEntries.length; i++) {
					html += `
						<div class="role-info-item">
							<span class="role-info-label">${roleEntries[i].label}:</span><span class="role-info-value">${roleEntries[i].value}</span>
						</div>
					`
				}
				html += `</div>`

				// Second column
				if (roleEntries.length > itemsPerColumn) {
					html += `<div class="role-info-column">`
					for (
						let i = itemsPerColumn;
						i < itemsPerColumn * 2 && i < roleEntries.length;
						i++
					) {
						html += `
							<div class="role-info-item">
								<span class="role-info-label">${roleEntries[i].label}:</span><span class="role-info-value">${roleEntries[i].value}</span>
							</div>
						`
					}
					html += `</div>`
				}

				// Third column
				if (roleEntries.length > itemsPerColumn * 2) {
					html += `<div class="role-info-column">`
					for (let i = itemsPerColumn * 2; i < roleEntries.length; i++) {
						html += `
							<div class="role-info-item">
								<span class="role-info-label">${roleEntries[i].label}:</span><span class="role-info-value">${roleEntries[i].value}</span>
							</div>
						`
					}
					html += `</div>`
				}

				html += `</div>`
				return html
			}

			const filteredNurseSchedule = nurseSchedule
				.filter(nurse => nurse.scheduledStartTime !== null)
				.map(nurse => ({
					...nurse,
					activePatients: getNursePatients(patientSchedule, nurse.nurseId)
						.filter(patient => !confirmedCancellationNoShow(patient))
						.sort(
							(a, b) =>
								startTimeAllCases(a, sandbox, isTomorrowMode) -
								startTimeAllCases(b, sandbox, isTomorrowMode)
						)
				}))
				.filter(nurse => nurse.activePatients.length > 0)

			// Calculate how many nurses to show per row (4 columns to fit on 2 pages)
			const nursesPerRow = 4

			// Get initials from name (first and last)
			const getInitials = name => {
				if (!name) return ""
				const parts = name.trim().split(" ")
				if (parts.length === 1) return parts[0].charAt(0)
				return parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
			}

			const printContent = `
				<!DOCTYPE html>
				<html>
				<head>
					<title>IC Schedule Print</title>
					<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet">
					<style>
						@media print {
							@page {
								size: landscape;
								margin: 0.5cm;
							}
							
							/* Force background colors to print */
							* {
								-webkit-print-color-adjust: exact !important;
								print-color-adjust: exact !important;
							}
						}
						
						:root {
							--custom-blue: #6955EA;
							--custom-blue-dark: #352D66;
							--gray-50: #f9fafb;
							--gray-100: #f3f4f6;
							--gray-200: #e5e7eb;
							--gray-300: #d1d5db;
							--gray-400: #9ca3af;
							--gray-500: #6b7280;
							--gray-600: #4b5563;
							--gray-700: #374151;
							--gray-800: #1f2937;
							--gray-900: #111827;
							--red-500: #ef4444;
							--red-600: #dc2626;
							--green-500: #10b981;
							--blue-500: #3b82f6;
						}
						
						body {
							font-family: 'Manrope', sans-serif;
							line-height: 1.5;
							margin: 0;
							padding: 12px;
							background-color: white;
							color: var(--gray-900);
							-webkit-font-smoothing: antialiased;
							-moz-osx-font-smoothing: grayscale;
							font-size: 14pt;
						}
						
						.print-header {
							display: flex;
							justify-content: space-between;
							align-items: center;
							margin-bottom: 20px;
							border-bottom: 2px solid var(--gray-200);
							padding-bottom: 12px;
						}
						
						.header-left {
							display: flex;
							align-items: center;
						}
						
						.logo {
							height: 36px;
							margin-right: 16px;
						}
						
						.print-title {
							font-size: 22pt;
							font-weight: 700;
							color: var(--gray-900);
						}
						
						.print-date {
							font-size: 16pt;
							font-weight: 600;
							color: var(--gray-700);
						}
						
						.print-charge-nurse {
							font-size: 16pt;
							font-weight: 500;
							color: var(--gray-700);
						}
						
						.header-right {
							text-align: right;
						}
						
						.print-container {
							display: flex;
							flex-direction: column;
							max-width: 100%;
						}
						
						.nurse-rows-container {
							display: flex;
							flex-direction: column;
						}
						
						.nurse-row {
							display: flex;
							flex-wrap: wrap;
							gap: 8px;
							margin-bottom: 8px;
							page-break-inside: avoid;
						}
						
						.nurse-card {
							width: calc(25% - 6px);
							background-color: white;
							border-radius: 8px;
							box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
							overflow: hidden;
							page-break-inside: avoid;
						}
						
						.nurse-header {
							display: flex;
							justify-content: space-between;
							align-items: center;
							padding: 8px 12px;
							background-color: var(--gray-50);
							border-bottom: 1px solid var(--gray-200);
						}
						
						.nurse-name-container {
							display: flex;
							align-items: center;
						}
						
						.nurse-icon {
							width: 24px;
							height: 24px;
							border-radius: 50%;
							background-color: var(--custom-blue);
							color: white;
							display: flex;
							align-items: center;
							justify-content: center;
							font-weight: 600;
							margin-right: 8px;
							font-size: 12px;
						}
						
						.nurse-name {
							font-weight: 600;
							font-size: 16pt;
							color: var(--gray-900);
						}
						
						.zone-badge {
							font-size: 9pt;
							padding: 2px 6px;
							border-radius: 4px;
							font-weight: 500;
						}
						
						.same-zone {
							background-color: #d1fae5 !important;
							color: #065f46 !important;
						}
						
						.two-zones {
							background-color: #fef3c7 !important;
							color: #92400e !important;
						}
						
						.multiple-zones {
							background-color: #fee2e2 !important;
							color: #b91c1c !important;
						}
						
						.nurse-info {
							padding: 8px 12px;
							font-size: 10pt;
							color: var(--gray-700);
							border-bottom: 1px solid var(--gray-200);
							display: flex;
							justify-content: space-between;
						}
						
						.nurse-info-item {
							margin-bottom: 0;
							white-space: nowrap;
							font-size: 14pt;
						}
						
						.nurse-info-label {
							font-weight: 700;
							color: var(--gray-800);
						}
						
						.nurse-info-value {
							color: var(--gray-900);
							margin-left: 4px;
						}
						
						.nurse-table-container {
							padding: 6px;
						}
						
						table {
							width: 100%;
							border-collapse: collapse;
							font-size: 13pt;
						}
						
						th {
							background-color: var(--gray-50);
							color: var(--gray-700);
							font-weight: 600;
							text-align: left;
							padding: 4px;
							border-bottom: 1px solid var(--gray-200);
						}
						
						td {
							padding: 4px;
							border-bottom: 1px solid var(--gray-200);
							vertical-align: middle;
						}
						
						tr:last-child td {
							border-bottom: none;
						}
						
						.time-cell {
							font-weight: 600;
							width: 15%;
						}
						
						.patient-cell {
							width: 30%;
							font-weight: 800;
							color: var(--gray-900);
						}
						
						.resource-cell {
							width: 30%;
							color: var(--gray-700);
						}
						
						.rx-cell {
							width: 25%;
							color: var(--gray-700);
						}
						
						.add-on {
							color: var(--red-600);
							font-weight: 600;
						}
						
						.completed {
							color: var(--blue-500) !important;
						}
						
						.ongoing {
							color: var(--green-500) !important;
						}
						
						.future {
							color: var(--gray-900) !important;
						}
						
						.cancelled {
							color: var(--gray-400) !important;
							text-decoration: line-through;
						}
						
						.empty-message {
							padding: 12px;
							text-align: center;
							color: var(--gray-500);
							font-style: italic;
						}
						
						.extra-notes {
							margin-top: 16px;
							padding: 16px;
							border-radius: 8px;
							border: 1px solid var(--gray-200);
						}
						
						.extra-notes-title {
							font-weight: 600;
							font-size: 12pt;
							color: var(--gray-800);
							margin-bottom: 12px;
							border-bottom: 1px solid var(--gray-200);
							padding-bottom: 8px;
						}
						
						.role-info-columns {
							display: flex;
							justify-content: space-between;
							gap: 24px;
							width: 100%;
						}
						
						.role-info-column {
							flex: 1;
							min-width: 0;
						}
						
						.role-info-item {
							margin-bottom: 8px;
							overflow: visible;
							word-wrap: break-word;
							word-break: break-word;
						}
						
						.role-info-label {
							font-weight: 700;
							color: var(--gray-800);
							display: inline;
						}
						
						.role-info-value {
							color: var(--gray-900);
							margin-left: 4px;
							display: inline;
							word-wrap: break-word;
							word-break: break-word;
						}
						
						.last-row {
							display: flex;
							flex-wrap: wrap;
							gap: 8px;
							width: 100%;
						}
						
						.staff-info-container {
							flex: 1;
							min-width: 0;
							padding-right: 8px;
						}
					</style>
				</head>
				<body>
					<div class="print-container">
						<div class="print-header">
							<div class="header-left">
								<img src="/images/oncare_logo.png" alt="Oncare Logo" class="logo">
								<div class="print-title">IC Schedule - ${chargeNurseName}</div>
							</div>
							<div class="header-right">
								<div class="print-date">${displayDate}</div>
							</div>
						</div>
						
						<div class="nurse-rows-container">
						${(() => {
							let html = ""

							// Calculate how many rows we need
							const rowCount = Math.ceil(filteredNurseSchedule.length / nursesPerRow)

							// For each row except the last one
							for (let i = 0; i < rowCount - 1; i++) {
								html += `<div class="nurse-row">`

								// For each column in this row
								for (let j = 0; j < nursesPerRow; j++) {
									const nurseIndex = i * nursesPerRow + j

									// If we have a nurse for this position
									if (nurseIndex < filteredNurseSchedule.length) {
										const nurse = filteredNurseSchedule[nurseIndex]
										const totalAcuity = calculateTotalAcuity(nurse, patientSchedule)

										const chairZones = nurse.activePatients
											.map(patient => getChairZone(patient.assignedChair))
											.filter(zone => zone)

										const uniqueZones = [...new Set(chairZones)].sort()
										const allSameZone =
											chairZones.length > 0 && uniqueZones.length === 1
										const hasMultipleZones = uniqueZones.length > 2
										const hasTwoZones = uniqueZones.length === 2

										let zoneBadgeClass = ""
										let zoneText = ""

										if (allSameZone) {
											zoneBadgeClass = "same-zone"
											zoneText = `Zone ${uniqueZones[0]}`
										} else if (hasTwoZones) {
											zoneBadgeClass = "two-zones"
											zoneText = `Zones ${uniqueZones.join(", ")}`
										} else if (hasMultipleZones) {
											zoneBadgeClass = "multiple-zones"
											zoneText = `Zones ${uniqueZones.join(", ")}`
										}

										html += `
											<div class="nurse-card">
												<div class="nurse-header">
													<div class="nurse-name-container">
														<div class="nurse-icon">${getInitials(nurse.nurseName)}</div>
														<div class="nurse-name">${nurse.nurseName}</div>
													</div>
													${
														chairZones.length > 0
															? `
														<span class="zone-badge ${zoneBadgeClass}">${zoneText}</span>
													`
															: ""
													}
												</div>
												<div class="nurse-info">
													<div class="nurse-info-item">
														<span class="nurse-info-label ml-3">Total Acuity:</span><span class="nurse-info-value">${totalAcuity}</span>
													</div>
													<div class="nurse-info-item">
														<span class="nurse-info-label">Shift:</span><span class="nurse-info-value">${convertMinutesTo12HHMM(nurse.scheduledStartTime)} - ${convertMinutesTo12HHMM(nurse.scheduledEndTime)}</span>
													</div>
												</div>
												<div class="nurse-table-container">
													${
														nurse.activePatients.length > 0
															? `
														<table>
															<thead>
																<tr>
																	<th>Time</th>
																	<th>Patient</th>
																	<th>Resource</th>
																	<th>Rx</th>
																</tr>
															</thead>
															<tbody>
																${nurse.activePatients
																	.map(patient => {
																		const isCompleted =
																			currentlyRealTimeCompleted(
																				patient
																			)
																		const isOngoing =
																			currentlyRealTimeOngoing(
																				patient
																			)
																		const isFuture =
																			currentlyRealTimeFuture(
																				patient,
																				sandbox,
																				isTomorrowMode
																			)
																		const isAddOnPatient =
																			isAddOn(
																				patient,
																				centerOpen,
																				testEnvironment,
																				isTomorrowMode,
																				centerClosedDates
																			)

																		let statusClass = ""
																		if (isCompleted)
																			statusClass =
																				"completed"
																		else if (isOngoing)
																			statusClass = "ongoing"
																		else if (isFuture)
																			statusClass = "future"

																		return `
																		<tr class="${statusClass}">
																			<td class="time-cell">${convertMinutesTo12HHMM(startTimeAllCases(patient, sandbox, isTomorrowMode))}</td>
																			<td class="patient-cell ${isAddOnPatient ? "add-on" : ""}">${patient.patientName}</td>
																			<td class="resource-cell">${getChairDetails(chairSchedule, patient.assignedChair) || "N/A"}</td>
																			<td class="rx-cell">${patient.infusionType ? `${patient.infusionType} (${patient.acuity}) ${patient.reasonForVisit ? `- ${toTitleCase(patient.reasonForVisit)}` : ""}` : "N/A"}</td>
																		</tr>
																	`
																	})
																	.join("")}
															</tbody>
														</table>
													`
															: `
														<div class="empty-message">No patients assigned</div>
													`
													}
												</div>
											</div>
										`
									} else {
										// Add an empty placeholder to maintain grid structure
										html += `<div class="nurse-card" style="visibility: hidden;"></div>`
									}
								}

								html += `</div>`
							}

							// Handle the last row with potential staff info
							if (rowCount > 0) {
								html += `<div class="last-row">`

								// Calculate how many nurses are in the last row
								const nursesInLastRow =
									filteredNurseSchedule.length - (rowCount - 1) * nursesPerRow

								// Add the nurses for the last row
								for (let j = 0; j < nursesInLastRow; j++) {
									const nurseIndex = (rowCount - 1) * nursesPerRow + j
									const nurse = filteredNurseSchedule[nurseIndex]
									const totalAcuity = calculateTotalAcuity(nurse, patientSchedule)

									const chairZones = nurse.activePatients
										.map(patient => getChairZone(patient.assignedChair))
										.filter(zone => zone)

									const uniqueZones = [...new Set(chairZones)].sort()
									const allSameZone =
										chairZones.length > 0 && uniqueZones.length === 1
									const hasMultipleZones = uniqueZones.length > 2
									const hasTwoZones = uniqueZones.length === 2

									let zoneBadgeClass = ""
									let zoneText = ""

									if (allSameZone) {
										zoneBadgeClass = "same-zone"
										zoneText = `Zone ${uniqueZones[0]}`
									} else if (hasTwoZones) {
										zoneBadgeClass = "two-zones"
										zoneText = `Zones ${uniqueZones.join(", ")}`
									} else if (hasMultipleZones) {
										zoneBadgeClass = "multiple-zones"
										zoneText = `Zones ${uniqueZones.join(", ")}`
									}

									html += `
										<div class="nurse-card">
											<div class="nurse-header">
												<div class="nurse-name-container">
													<div class="nurse-icon">${getInitials(nurse.nurseName)}</div>
													<div class="nurse-name">${nurse.nurseName}</div>
												</div>
												${
													chairZones.length > 0
														? `
													<span class="zone-badge ${zoneBadgeClass}">${zoneText}</span>
												`
														: ""
												}
											</div>
											<div class="nurse-info">
												<div class="nurse-info-item">
													<span class="nurse-info-label ml-3">Total Acuity:</span><span class="nurse-info-value">${totalAcuity}</span>
												</div>
												<div class="nurse-info-item">
													<span class="nurse-info-label">Shift:</span><span class="nurse-info-value">${convertMinutesTo12HHMM(nurse.scheduledStartTime)} - ${convertMinutesTo12HHMM(nurse.scheduledEndTime)}</span>
												</div>
											</div>
											<div class="nurse-table-container">
												${
													nurse.activePatients.length > 0
														? `
													<table>
														<thead>
															<tr>
																<th>Time</th>
																<th>Patient</th>
																<th>Resource</th>
																<th>Rx</th>
															</tr>
														</thead>
														<tbody>
															${nurse.activePatients
																.map(patient => {
																	const isCompleted =
																		currentlyRealTimeCompleted(
																			patient
																		)
																	const isOngoing =
																		currentlyRealTimeOngoing(
																			patient
																		)
																	const isFuture =
																		currentlyRealTimeFuture(
																			patient,
																			sandbox,
																			isTomorrowMode
																		)
																	const isAddOnPatient = isAddOn(
																		patient,
																		centerOpen,
																		testEnvironment,
																		isTomorrowMode,
																		centerClosedDates
																	)

																	let statusClass = ""
																	if (isCompleted)
																		statusClass = "completed"
																	else if (isOngoing)
																		statusClass = "ongoing"
																	else if (isFuture)
																		statusClass = "future"

																	return `
																	<tr class="${statusClass}">
																		<td class="time-cell">${convertMinutesTo12HHMM(startTimeAllCases(patient, sandbox, isTomorrowMode))}</td>
																		<td class="patient-cell ${isAddOnPatient ? "add-on" : ""}">${patient.patientName}</td>
																		<td class="resource-cell">${getChairDetails(chairSchedule, patient.assignedChair) || "N/A"}</td>
																		<td class="rx-cell">${patient.infusionType ? `${patient.infusionType} (${patient.acuity}) ${patient.reasonForVisit ? `- ${toTitleCase(patient.reasonForVisit)}` : ""}` : "N/A"}</td>
																	</tr>
																`
																})
																.join("")}
														</tbody>
													</table>
												`
														: `
													<div class="empty-message">No patients assigned</div>
												`
												}
											</div>
										</div>
									`
								}

								// If there's space in the last row, add the staff info there
								if (
									nursesInLastRow < nursesPerRow &&
									Object.keys(roleInfo).length > 0
								) {
									const remainingWidth =
										((nursesPerRow - nursesInLastRow) / nursesPerRow) * 100
									html += `
										<div class="staff-info-container" style="width: ${remainingWidth}%;">
											<div class="extra-notes">
												<div class="extra-notes-title">Additional Staff Information</div>
												${formatRoleInfoInColumns(roleInfo)}
											</div>
										</div>
									`
								}

								html += `</div>`

								// If there was no space in the last row, add the staff info in a new row
								if (
									nursesInLastRow === nursesPerRow &&
									Object.keys(roleInfo).length > 0
								) {
									html += `
										<div class="extra-notes">
											<div class="extra-notes-title">Additional Staff Information</div>
											${formatRoleInfoInColumns(roleInfo)}
										</div>
									`
								}
							}

							return html
						})()}
						</div>
					</div>
					<script>
						window.onload = function() {
							window.print();
						}
					</script>
				</body>
				</html>
			`

			printWindow.document.open()
			printWindow.document.write(printContent)
			printWindow.document.close()
		}

		// Start the process by fetching role info
		fetchRoleInfo()
	}

	const toTitleCase = string => {
		return string
			.toLowerCase()
			.split(" ")
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ")
	}

	return (
		<>
			<div
				aria-live="assertive"
				className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-[9999]"
			>
				<div className="flex w-full flex-col items-center space-y-4 sm:items-end">
					<Transition show={showNotification}>
						<div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition data-[closed]:data-[enter]:translate-y-2 data-[enter]:transform data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-x-2 data-[closed]:data-[enter]:sm:translate-y-0">
							<div className="p-4">
								<div className="flex items-start">
									<div className="shrink-0">
										<CiStickyNote
											aria-hidden="true"
											className="size-6 text-green-400"
										/>
									</div>
									<div className="ml-3 w-0 flex-1 pt-0.5">
										<p className="text-sm font-medium text-gray-900 text-left">
											Information
										</p>
										<p className="mt-1 text-sm text-gray-500 text-left">
											Click on any patient row to edit their details before
											their appointment has been completed.
										</p>
										<p className="mt-1 text-sm text-gray-500 text-left mt-4">
											Here are what the colors means for the time:
										</p>
										<ul className="list-disc list-inside text-sm text-left">
											<li className="text-green-500">Ongoing</li>
											<li className="text-blue-500">Completed</li>
											<li className="text-black">Not started</li>
											<li className="text-gray-400">
												Cancellations or No shows
											</li>
										</ul>
										<p className="text-sm text-gray-500 text-left mt-2">
											A green outline around a nurse's section indicates that
											all assigned chairs are located within the same zone.
										</p>
									</div>
									<div className="ml-4 flex shrink-0">
										<button
											type="button"
											onClick={closeNotification}
											className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
										>
											<span className="sr-only">Close</span>
											<IoClose aria-hidden="true" className="size-5" />
										</button>
									</div>
								</div>
							</div>
						</div>
					</Transition>
				</div>
			</div>

			<div className="mb-4 flex justify-between my-4">
				<PatientNurseStats
					patientSchedule={patientSchedule}
					nurseSchedule={nurseSchedule}
				/>

				{/* Add print button */}
				<button
					onClick={handlePrint}
					className="flex items-center gap-2 px-4 py-2 bg-customBlue text-white rounded-md hover:bg-customBlue-dark transition duration-200"
					aria-label="Print nurse schedules"
				>
					<FaPrint className="size-4" />
					<span>Print</span>
				</button>
			</div>

			{/* Search bar on its own line */}
			<div className="flex justify-between items-center mb-4">
				<SearchBar
					searchableItems={[
						{
							items: nurseSchedule,
							type: "nurse",
							getSearchableText: nurse => nurse.nurseName,
							getId: nurse => nurse.nurseId,
							getName: nurse => nurse.nurseName,
							getDisplayText: nurse => "Nurse",
							iconBgColor: "bg-blue-100",
							iconTextColor: "text-blue-600"
						},
						{
							items: patientSchedule,
							type: "patient",
							getSearchableText: patient =>
								`${patient.patientName} ${patient.infusionType || ""}`,
							getId: patient => patient.patientId,
							getName: patient => patient.patientName,
							getDisplayText: patient => patient.infusionType || "No type",
							iconBgColor: "bg-green-100",
							iconTextColor: "text-green-600"
						}
					]}
					placeholder="Search nurses or patients..."
					onResultClick={handleSearchResultClick}
					width="w-1/3"
				/>

				<div className="flex items-center space-x-4">
					<StatusFilter
						selectedStatuses={selectedStatuses}
						onChange={setSelectedStatuses}
					/>
					<div className="flex items-center">
						<input
							type="number"
							value={inputColumnValue}
							onChange={handleInputChange}
							onKeyDown={e => {
								if (e.key === "Enter") {
									updateColumnCount()
								}
							}}
							className="w-auto max-w-[50px] px-3 py-1.5 text-center text-gray-700 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-0"
						/>
						<button
							onClick={updateColumnCount}
							className="ml-2 p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-200"
							aria-label="Update the number of columns for the grid view"
						>
							<ViewColumnsIcon className="size-4" />
						</button>
					</div>
				</div>
			</div>

			<div
				className={`grid grid-cols-${columnCount} gap-4 grid-view-container`}
				ref={container}
			>
				{gridViewNurseSchedule.map((nurse, index) => {
					// IMPORTANT: Use the current nurseSchedule prop to get the latest nurse data
					// This prevents sync issues between state and props during React updates
					const currentNurse =
						nurseSchedule.find(n => n.nurseId === nurse.nurseId) || nurse

					const allNursePatients = getNursePatients(
						patientSchedule,
						currentNurse.nurseId
					).sort(
						(a, b) =>
							startTimeAllCases(a, sandbox, isTomorrowMode) -
							startTimeAllCases(b, sandbox, isTomorrowMode)
					)

					// Filter nurse's patients based on selected statuses (for display)
					const nursePatients = getFilteredPatients(allNursePatients, selectedStatuses)

					// Get only active (non-cancelled) patients for calculations
					const activeNursePatients = allNursePatients.filter(
						patient => patient && !confirmedCancellationNoShow(patient)
					)

					// Calculate acuity for the current nurse
					const instantaneousAcuity = calculateNurseAcuity(
						[currentNurse], // Pass the current nurse instead of resource
						patientSchedule,
						undefined,
						undefined,
						sandbox,
						isTomorrowMode
					)[0].currentAcuity

					const MAFNAT = settingsData?.maxAcuityForNurseAtAnyTime || 9
					const totalAcuity = calculateTotalAcuity(currentNurse, patientSchedule) // Pass the current nurse
					const MAFN = settingsData?.maxAcuityForNurse || 15
					const overCapacity = totalAcuity > MAFN || instantaneousAcuity > MAFNAT

					const chairZones =
						Array.isArray(activeNursePatients) && activeNursePatients.length
							? activeNursePatients.map(patient =>
									getChairZone(patient.assignedChair)
								)
							: []

					const allSameZone =
						chairZones.length > 0 && chairZones.every(zone => zone === chairZones[0])

					const uniqueZones = [...new Set(chairZones)].sort()
					const hasMultipleZones = uniqueZones.length > 2

					return (
						<div
							key={nurse.nurseId} // Use a stable unique key here
							data-swapy-slot={index}
							data-swapy-highlighted
							data-swapy-dragging={isDragging && draggingItem === nurse.nurseId}
							className={`grid-view-slot flex flex-col h-full rounded-2xl ${animatedClass} ${isDragging ? "cursor-grabbing" : "cursor-pointer"}`}
						>
							<div
								className={`grid-view-slot-item bg-white shadow-xl rounded-2xl p-4 min-w-0 flex-1 ${allSameZone ? "border-2 border-green-500" : ""}`}
								data-swapy-item={nurse.nurseId}
							>
								<h2 className="text-2xl text-left font-semibold mb-2 flex justify-start items-center">
									<div data-swapy-handle>
										<RxDragHandleDots2 />
									</div>
									<div className="flex items-center gap-2">
										<IoPersonCircle className="size-6" />
										<span className="font-bold">{currentNurse.nurseName}</span>
									</div>
									<div className="flex items-center justify-end flex-grow">
										<span className="text-sm flex items-center">
											{allSameZone && chairZones.length > 0 && (
												<span className="bg-green-100 text-green-700 px-3 py-1 rounded-md mr-2 flex items-center w-fit">
													<IoCheckmark className="size-4 mr-1" />
													All chairs in Zone{" "}
													{getChairZone(
														activeNursePatients[0]?.assignedChair
													)}
												</span>
											)}
											{!allSameZone &&
												!hasMultipleZones &&
												chairZones.length > 0 && (
													<span className="text-black px-3 py-1 rounded-md mr-2 flex items-center w-fit">
														Chairs across zones {uniqueZones.join(", ")}
													</span>
												)}
											{hasMultipleZones && (
												<span className="bg-red-100 text-red-700 px-3 py-1 rounded-md mr-2 flex items-center w-fit">
													<FaExclamationCircle className="size-4 mr-1" />
													Chairs across zones {uniqueZones.join(", ")}
												</span>
											)}
										</span>
									</div>
								</h2>
								<h3 className="text-lg font-medium mb-1 text-left">
									Current Acuity: {instantaneousAcuity} • Total Acuity:{" "}
									{totalAcuity}
								</h3>
								<h4 className="text-sm font-light mb-2 text-left">
									Shift: {convertMinutesTo12HHMM(currentNurse.scheduledStartTime)}
									- {convertMinutesTo12HHMM(currentNurse.scheduledEndTime)}
								</h4>
								{overCapacity && (
									<div
										className="bg-red-100 border border-red-400 text-red-700 px-4 py-1 rounded relative ml-4 flex items-center text-sm"
										role="alert"
									>
										<FaCircleExclamation
											aria-hidden="true"
											className="h-5 w-5 text-red-500 mr-2"
										/>
										<span className="block sm:inline ml-2">
											This nurse is currently over capacity.
										</span>
									</div>
								)}
								<table className="table-fixed w-full text-[1.2vw] md:text-sm lg:text-base text-left text-gray-500">
									<thead className="text-[1.2vw] md:text-sm lg:text-lg text-gray-700 uppercase bg-gray-100">
										<tr>
											<th
												scope="col"
												className={`w-${columnCount === 4 ? "1/4" : "1/3"} px-[0.5vw] md:px-4 py-[0.5vw] md:py-4`}
											>
												Time
											</th>
											<th
												scope="col"
												className={`w-${columnCount === 4 ? "1/4" : "1/4"} px-[0.5vw] md:px-4 py-[0.5vw] md:py-4`}
											>
												Patient
											</th>
											<th
												scope="col"
												className={`w-${columnCount === 4 ? "1/4" : "1/4"} px-[0.5vw] md:px-4 py-[0.5vw] md:py-4`}
											>
												Resource
											</th>
											<th
												scope="col"
												className={`w-${columnCount === 4 ? "1/4" : "1/3"} px-[0.5vw] md:px-4 py-[0.5vw] md:py-4`}
											>
												Rx
											</th>
										</tr>
									</thead>
									<tbody>
										{nursePatients.map((patient, pIndex) => {
											// Determine if the patient is a no-show or cancellation
											return (
												<tr
													key={pIndex}
													className={`bg-white border-b hover:bg-gray-50 ${confirmedCancellationNoShow(patient) ? "line-through opacity-50" : ""}`}
													onClick={() => {
														openEditPopup(patient)
													}}
												>
													<td
														className={`w-${columnCount === 4 ? "1/6" : "1/4"} px-[0.5vw] md:px-4 py-[0.5vw] md:py-4 whitespace-nowrap text-left text-lg ${currentlyRealTimeCompleted(patient) ? "text-blue-500" : currentlyRealTimeOngoing(patient) ? "text-green-500" : currentlyRealTimeFuture(patient, sandbox, isTomorrowMode) ? "text-gray-800" : "text-gray-400"}`}
													>
														<div className="flex items-center">
															<span>
																{convertMinutesTo12HHMM(
																	startTimeAllCases(
																		patient,
																		sandbox,
																		isTomorrowMode
																	)
																)}
															</span>

															{currentlyRealTimeOngoing(patient) ? (
																<span className="animate-pulse ml-2 h-3 w-3 rounded-full bg-green-500"></span>
															) : (
																currentlyRealTimeCompleted(
																	patient
																) && (
																	<IoCheckmark className="ml-2" />
																)
															)}
														</div>
													</td>
													<td
														className={`
															sensitive
															w-${columnCount === 4 ? "1/4" : "1/3"} 
															px-[0.5vw] md:px-4 
															py-[0.5vw] md:py-4 
															font-medium 
															${isAddOn(patient, centerOpen, testEnvironment, isTomorrowMode, centerClosedDates) ? "text-red-600" : "text-gray-900"}
														`}
													>
														{patient.patientName}
													</td>
													<>
														<td className="w-1/12 px-[0.5vw] md:px-4 py-[0.5vw] md:py-4">
															{getChairDetails(
																chairSchedule,
																patient.assignedChair
															) || "N/A"}
														</td>
														<td className="w-1/4 px-[0.5vw] md:px-4 py-[0.5vw] md:py-4 break-words">
															{patient.infusionType ? (
																<div>
																	<div>{`${patient.infusionType} (${patient.acuity})`}</div>
																	{patient.reasonForVisit && (
																		<div className="mt-1 text-[0.6rem] text-gray-500">
																			{toTitleCase(
																				patient.reasonForVisit
																			)}
																		</div>
																	)}
																</div>
															) : (
																"N/A"
															)}
														</td>
													</>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						</div>
					)
				})}
			</div>

			{isEditOpen && taskToEdit && (
				<EditPatient
					patient={taskToEdit}
					isOpen={
						(hasPermission(permissions, "write:schedules") ||
							hasPermission(permissions, "write:chairs") ||
							hasPermission(permissions, "read:schedule-personal") ||
							sandbox) &&
						allowedToEdit(taskToEdit, sandbox, isTomorrowMode)
					}
					schedule={currentSchedule}
					onClose={closeEditPopup}
					scheduleSelection={scheduleSelection}
					permissions={permissions}
					setNotification={setNotification}
				/>
			)}
		</>
	)
}
